<template>
  <main>
    <GameHeader
      :canBackward="canBackward"
      :canForward="canForward"
      :active="showHelp"
      :title="sound && sound.name"
      @help="onUseHelpEye"
      @backward="onChangeSound($metrika.consts.GAME_BACKWARD, backSound)"
      @forward="onChangeSound($metrika.consts.GAME_FORWARD, nextSound)"
    >
      <AppSelect
        size="small"
        keyword="name"
        class="difficulty"
        :options="difficulties"
        :value="currentDifficulty.toString()"
        @change="onChangeLevel"
      >
        <template #option="{ option }">
          Сложность: {{ option.name }}
        </template>
      </AppSelect>
    </GameHeader>

    <Preloader v-if="!sound" absolute/>
    <GameField
      v-else
      :sound="sound"
      :user="user"
      @finish="onFinish"
    />
  </main>
</template>


<script>
import _ from 'lodash';
import axios from 'axios';
import GameHeader from './GameHeader';
import GameField from './GameField';
import Preloader from '@/components/game/Preloader';
import AppSelect from '@/components/game/ui/AppSelect';

import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

export default {
  name: 'AppGame_ViewSound',

  components: {
    Preloader,
    GameHeader,
    GameField,
    AppSelect,
  },

  data: () => ({
    page: 'sound',
    showHelp: false,
    authors: {},
    scores: {},
    info: {},

    isEditing: true,
    currentLibrary: null,
    audio: null,
    isShowLevelControl: false,
  }),

  watch: {
    'sound._id'(soundId) {
      if (!soundId) return;
      this.showHelp = !!this.sound.name;
      this.showHelp && this.setShowSoundName(true);
    },
  },

  computed: {
    ...mapState({ completedSounds: state => state.game.completedSounds }),
    ...mapGetters([
      'difficultyStatuses',
      'currentSoundIndex',
      'soundOfHistory',
      'historySounds',
      'axiosOptions',
      'orientation',
      'settingsGame',
      'selectedStock',
      'settings',
      'library',
      'scoresValues',
      'sound',
      'user',
      'token',
      'loading',
      'tokenGame',
      'difficulties',
      'startGameTime',
      'currentDifficulty',
      'sumGuessedSounds',
    ]),
    canBackward() {
      return this.currentSoundIndex > 0 || this.difficultyStatuses.loadAllSounds;
    },
    canForward() {
      return !this.loading && !!this.existNextSound;
    },
    existNextSound() {
      const nextSound = this.historySounds?.[this.currentSoundIndex + 1];
      return this.sound || (nextSound || {}).id;
    },
  },

  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav);
  },

  async mounted() {
    this.createGameDuration();
    this.getGameSettings();
    this.getGameLibrary();
    this.getGameDifficulties();
    await this.getGameSounds({ withToken: true });
    this.$metrika.add(this.$metrika.consts.GAME_START);
  },


  async beforeDestroy() {
    window.removeEventListener('beforeunload', this.preventNav);
  },

  beforeRouteLeave(to, from, next) {
    next();
  },

  methods: {
    ...mapMutations([
      'updateCompletedSounds',
    ]),
    ...mapActions([
      'getGameSettings',
      'getGameDifficulties',
      'getGameLibrary',
      'getGameScores',
      'getGameSpeeds',
      'getGameSounds',
      'onSoundSkip',
      'createGameDuration',
      'useNameTipBySoundId',
      'setShowSoundName',
      'addGuessedSound',
      'prepareToNextGame',
      'nextSound',
      'backSound',
      'changeDifficultyLevel',
      'logout',
    ]),

    hideSoundName() {
      this.setShowSoundName(false);
      this.showHelp = false;
    },

    async onUseHelpEye() {
      if (!this.sound.name) await this.useNameTipBySoundId();
      const isShow = !this.soundOfHistory.showName;
      this.showHelp = isShow;
      isShow && this.$metrika.add(this.$metrika.consts.GAME_USE_HELP_EYE);
      this.setShowSoundName(isShow);
    },

    onChangeLevel(value) {
      if (this.currentDifficulty > value) return this.onLevelDown(value);
      if (this.currentDifficulty < value) return this.onLevelUp(value);
    },

    onLevelUp(value) {
      if (this.currentDifficulty >= 5) return;
      this.onChangeSound(this.$metrika.consts.GAME_LEVEL_UP, this.nextSound.bind(this));
      this.changeDifficultyLevel(value || (this.currentDifficulty + 1));
      this.updateCompletedSounds({ sameLevel: 0 });
      this.showHelp = false;
    },

    onLevelDown(value) {
      if (this.currentDifficulty <= 1) return;
      this.onChangeSound(this.$metrika.consts.GAME_LEVEL_DOWN, this.nextSound.bind(this));
      this.changeDifficultyLevel(value || (this.currentDifficulty - 1));
      this.updateCompletedSounds({ sameLevel: 0 });
      this.showHelp = false;
    },

    onChangeSound(eventName, callback = _.noop) {
      const prevTrackGamePayload = _.cloneDeep(this.$store.getters['metrika/currentGamePayload']);
      this.updateCompletedSounds({ inRow: 0 });
      setTimeout(() => {
        callback();
        this.$metrika.add(eventName);
        this.sendTookMetrika(prevTrackGamePayload);
      });
    },

    sendTookMetrika(prevTrackGamePayload) {
      const timeDifferent = Date.now() - (this.startGameTime || Date.now());
      this.createGameDuration();
      if (!timeDifferent) return;
      const tookSecond = Math.floor(timeDifferent / 1000);
      this.$metrika.add(
        this.$metrika.consts.GAME_SOUND_TIMECOST, 
        _.defaultsDeep({ game: _.cloneDeep(prevTrackGamePayload) }, { game: { tookSecond } }), 
        [0, 1, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 600].reverse().find(t => t <= tookSecond),
      );
      this.hideSoundName();
    },

    async onFinish(event) {
      await this.$emit('finish:sound', event);
      this.sendTookMetrika(this.$store.getters['metrika/currentGamePayload']);
    },

    // async finishSound({ time, words }) {
    //   try {
    //     this.info = { words };
    //     this.info.date = DateFilter(new Date(), 'datehour', true, true);
    //     this.info.duration = Math.ceil((time.end - time.start) / 1000);
    //     this.$metrika.add(this.$metrika.consts.GAME_SOUND_FINISH);
    //     this.authors = await this.getAuthorsBySoundId();
    //     this.updateCompletedSounds({
    //       perSession: 1 + this.completedSounds.perSession,
    //       sameLevel: 1 + this.completedSounds.sameLevel,
    //       inRow: 1 + this.completedSounds.inRow,
    //     });
    //     this.sendTookMetrika(this.$store.getters['metrika/currentGamePayload']);
    //     if (this.authors.length) return (this.page = 'author');
    //     this.finishAuthor(null);
    //   // this.addGuessedSound();
    //   // this.dataCollection(date, seconds, data);
    //   } catch(err) {
    //     console.error(err);
    //   };
    // },

    // async finishAuthor(authorId) {
    //   try {
    //     const payload = Object.assign({ authorId }, this.info);
    //     const response = await this.finishGame(payload);
    //     this.scores = response.scores;
    //     this.page = 'finish';
    //     const game = { authorId };
    //     game.scores = _.omit(response.scores.sound, ['difficultyCoefficient', 'speedCoefficient']);
    //     this.$metrika.add(this.$metrika.consts.GAME_AUTHOR_FINISH, { game });
    //   } catch(err) {
    //     console.error(err);
    //   };
    // },

    // Отправка метрики, вшить в событие finish
    // dataCollection(date, seconds, data) {
    //   try {
    //     const body = { date, seconds };
    //     body.quantity_currect_answer = data.quantity_currect_answer;
    //     body.quantity_helps = data.quantity_helps;
    //     body.sound_id = this.sound._id;
    //     axios.put(`/api/analytics/sound`, body, this.axiosOptions);
    //   } catch(err) {
    //     console.error(err);
    //   };
    // },
    // Надо переделать
    async onNextGame() {
      this.page = 'loading';
      this.$metrika.add(this.$metrika.consts.GAME_CONTINUE);
      await this.prepareToNextGame();
      await this.getGameSounds({ limit: 1 });
      this.page = 'sound';
      this.hideSoundName();
    },
  },
};
</script>


<style lang="scss" scoped>
main {
  .difficulty {
    width: 200px;
    max-width: inherit;
    color: var(--color-secondary);
  }
}
</style>